// Can 47 (the test iPhone, 18 Oct 2026): the card for making a Canvas listed every argument, the page's whole HTML included. A
// Canvas create or save in the person's words: its name, what it shows, its type, its files by name and size, who can see it.
// One set of words for the Desk's Inbox and Pocket's card; the exact request stays one tap away on both. Pure.

export interface PlainCanvasWrite {
  readonly headline: string;
  readonly rows: ReadonlyArray<readonly [label: string, text: string]>;
}

/** A name as one short line: no control or format characters (bidi), no quote marks that could fake the closing quote. */
export function canvasNameLine(raw: unknown): string {
  if (typeof raw !== "string") return "";
  const clean = raw
    .replace(/[\p{Cc}\p{Cf}\s]+/gu, " ")
    .replace(/[“”„‟"]/gu, "")
    .trim();
  return clean.length > 60 ? `${clean.slice(0, 59).trimEnd()}…` : clean;
}

const count = (n: number) => n.toLocaleString("en-US");

function fileLine(file: unknown): string | null {
  if (file === null || typeof file !== "object") return null;
  const entry = file as Record<string, unknown>;
  if (typeof entry["library"] === "string") return `${entry["library"]}, a pinned library`;
  if (typeof entry["path"] !== "string") return null;
  if (typeof entry["text"] === "string") return `${entry["path"]}, ${count(entry["text"].length)} characters`;
  if (typeof entry["sha256"] === "string") return `${entry["path"]}, a stored file`;
  return entry["path"];
}

/** A neucleos_canvas create or save in words, or null for any other action (or a call these words cannot read). */
export function plainCanvasWrite(input: Record<string, unknown>): PlainCanvasWrite | null {
  const action = input["action"];
  if (action !== "create" && action !== "save") return null;
  const name = canvasNameLine(input["title"]);
  const what = typeof input["description"] === "string" && input["description"].trim() ? input["description"].trim() : "No description.";
  // A call that does not name its files (or a create that names no title) is not described: the exact request says what it is.
  if (!Array.isArray(input["files"]) || input["files"].length === 0) return null;
  if (action === "create" && !name) return null;
  if (action === "save" && typeof input["canvasId"] !== "string") return null;
  const files = input["files"].map(fileLine);
  if (files.some((line) => line === null)) return null;
  const filesText = files.length ? (files as string[]).join("\n") : "No files.";
  if (action === "create")
    return {
      headline: name ? `Make the Canvas “${name}”` : "Make a Canvas",
      rows: [
        ["What", what],
        ...(typeof input["type"] === "string" ? ([["Type", input["type"]]] as const) : []),
        ["Files", filesText],
        ["Who can see it", "Only you. A new Canvas is private until you share it."],
      ],
    };
  const head = input["expectedHeadSequence"];
  return {
    headline: name ? `Change the Canvas “${name}”` : "Change a Canvas",
    rows: [
      ["What", what],
      ["Files", filesText],
      [
        "Version",
        typeof head === "number" && Number.isSafeInteger(head)
          ? `Version ${head + 1}, after version ${head}. Earlier versions are kept.`
          : "A new version. Earlier versions are kept.",
      ],
      ["Who can see it", "Everyone who can see this Canvas now sees the new version."],
    ],
  };
}
